import { useState } from 'react'
import { ISortOrderEnum } from 'types/graphqlTypes'
import { useTrailersQuery } from '../graphql/Trailers'

export const ITEMS_PER_PAGE = 16

export interface ITrailersFilters {
  /**
   * String of carrier id to filter by
   * @example "123"
   */
  carrierId?: string
  /**
   * Array of trailer states to filter by
   * @example ["inactive", "unassigned"]
   */
  states?: string[]
  /**
   * Cursor to use as delimiter for paginated query, obtained from query, equal to base64 representation of trailer id
   * @example "MQ" (Equal to 1 in base64)
   */
  after?: string
  /**
   * Cursor to use as delimiter for paginated query, obtained from query, equal to base64 representation of trailer id
   * @example "Mw" (Equal to 3 in base64)
   */
  before?: string
  /**
   * Amount of items to receive from paginated query, query will return first n items
   * @example 16
   */
  first?: number
  /**
   * Amount of items to receive from paginated query, query will return last n items
   * @example 16
   */
  last?: number
  /**
   * String of term to search
   * @example "123"
   */
  searchTerm?: string
  /**
   * Field to sort by
   * @example "id"
   */
  sortBy?: string
  /**
   * Order to be used when sorting resulting items, defaults to DESC
   * @example "ASC"
   */
  sortDirection?: ISortOrderEnum
}

interface IUseTrailersParams {
  /**
   * Filters to be sent as variables in graphql query
   * @example { carrierIds: ["123"], states: ["empty_at_delivery"] }
   * @default {}
   */
  filters?: ITrailersFilters
  /**
   * Amount of items to get per page from paginated query
   * @example 10
   * @default 16
   */
  itemsPerPage?: number
}

export const useTrailers = ({
  filters: defaultFilters = {},
  itemsPerPage = ITEMS_PER_PAGE,
}: IUseTrailersParams) => {
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [filters, setFilters] = useState<ITrailersFilters>(defaultFilters)

  const {
    after,
    before,
    carrierId,
    first,
    last,
    searchTerm,
    sortBy,
    sortDirection,
    states,
  } = filters

  const { data, loading } = useTrailersQuery({
    variables: {
      carrierId: carrierId || '',
      states,
      after,
      before,
      ...(!before ? { first: first || itemsPerPage } : {}),
      ...(before ? { last: last || itemsPerPage } : {}),
      searchTerm: searchTerm || '',
      sortDirection: sortDirection || ISortOrderEnum.DESC,
      sortBy,
    },
  })

  return {
    endCursor: data?.searchTrailers.pageInfo.endCursor,
    filters,
    loading,
    pageNumber,
    setFilters,
    setPageNumber,
    startCursor: data?.searchTrailers.pageInfo.startCursor,
    totalCount: data?.searchTrailers.totalCount || data?.searchTrailers.nodes?.length || 0,
    trailers: data?.searchTrailers.nodes,
  }
}
